import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance, { clearAuthData, isJwtAuthFailure } from "../../components/content/axiosConfig";
import { enqueueSnackbar } from "notistack";
import Swal from 'sweetalert2';
import Preloader from "../../components/custom/preLoader";
import ListPagination from "../../components/custom/ListPagination";
import CustomModal from "../../components/custom/customModal";
import useServerPaginatedList from "../../hooks/useServerPaginatedList";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";

const LengthUnits = () => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modalData, setModalData] = useState({
        Name: "",
        Type: ""
    });
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);

    const fetchLengthUnitsPage = useCallback(async ({ search, page, limit }) => {
        try {
            return await axiosInstance.get(`/api/LengthUnit/GetAll`, {
                params: { search, page, limit },
            });
        } catch (error) {
            if (isJwtAuthFailure(error)) {
                clearAuthData();
                window.location.href = "/user/login";
                return { data: { data: [], total: 0 } };
            }
            const errorMessage = error.response?.data?.message || "An error occurred.";
            enqueueSnackbar(errorMessage, {
                variant: 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'right' },
                autoHideDuration: 3000,
            });
            throw error;
        }
    }, []);

    const {
        rows: fishTypes,
        totalCount,
        searchTerm,
        setSearchTerm,
        currentPage,
        setCurrentPage,
        isLoading,
        entriesPerPage,
        refetch,
    } = useServerPaginatedList(fetchLengthUnitsPage);

    const DeleteUser = async (unitId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        });

        if (result.isConfirmed) {
            try {
                setIsSubmitting(true);
                await axiosInstance.delete(`/api/LengthUnit/Delete/${unitId}`);
                showSuccessToast("Length unit deleted successfully");
                await refetch();
            } catch (error) {
                if (isJwtAuthFailure(error)) {
                    clearAuthData();
                    window.location.href = "/user/login";
                    return;
                }
                showErrorToast(error.response?.data?.message || "An error occurred.");
                console.error("Error deleting length unit:", error);
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const openModal = (data = null) => {
        if (data) {
            setIsEditing(true);
            setEditId(data.id);
            setModalData({ Name: data.Name });
        } else {
            setIsEditing(false);
            setEditId(null);
            setModalData({ Name: "" });
        }
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
    };

    const handleChange = (e) => {
        const { id, name, value, type, checked } = e.target;
        if (type === "radio") {
            setModalData({
                ...modalData,
                [name]: value === "true",
            });
        } else {
            setModalData({
                ...modalData,
                [id]: type === "checkbox" ? checked : value,
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!String(modalData.Name || "").trim()) {
            showErrorToast("Name is required");
            return;
        }
        try {
            setIsSubmitting(true);
            if (isEditing) {
                await axiosInstance.put(`/api/LengthUnit/Update/${editId}`, modalData);
                showSuccessToast("Length unit updated successfully");
            } else {
                await axiosInstance.post(`/api/LengthUnit/Add`, modalData);
                showSuccessToast("Length unit added successfully");
            }
            await refetch();
            closeModal();
        } catch (error) {
            if (isJwtAuthFailure(error)) {
                clearAuthData();
                window.location.href = "/user/login";
                return;
            }
            showErrorToast(error.response?.data?.message || "An error occurred.");
            console.error("Error saving length unit:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading && fishTypes.length === 0) {
        return <Preloader />;
    }

    return (
        <div>
            <div className="d-flex align-items-center mb-3">
                <div>
                    <ul className="breadcrumb">
                        <li className="breadcrumb-item">
                            <Link to="/">Home</Link>
                        </li>
                        <li className="breadcrumb-item active" />
                    </ul>
                    <h1 className="page-header mb-0">Length Unit</h1>
                </div>
                <div className="ms-auto">
                    <button
                        type="button"
                        className="btn btn-success btn-rounded px-4 rounded-pill"
                        onClick={() => openModal()}
                        aria-label="Add length unit"
                        data-testid="add-length-unit"
                    >
                        <i className="fa fa-plus fa-lg me-2 ms-n2 text-success-900" aria-hidden="true"></i>
                        Add
                    </button>
                </div>
            </div>
            <div className="card border-0">
                <div className="tab-content p-3">
                    <div className="tab-pane fade show active" id="allTab">
                        <div className="input-group mb-3">
                            <div className="flex-fill position-relative">
                                <div className="input-group">
                                    <div
                                        className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                                        style={{ zIndex: 10 }}
                                    >
                                        <i className="fa fa-search opacity-5"></i>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control px-35px bg-light"
                                        placeholder="Search Length Unit..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="table-responsive mb-3">
                            <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan={2} className="text-center text-muted py-5">
                                                Loading…
                                            </td>
                                        </tr>
                                    ) : (
                                        fishTypes.map((web) => (
                                            <tr key={web.id}>
                                                <td>{web.Name}</td>
                                                <td>
                                                    <span onClick={() => openModal(web)}>
                                                        <i className="fa fa-pencil fs-13px fa-fw me-5px"></i>
                                                    </span>
                                                    <span>
                                                        <i
                                                            onClick={() => DeleteUser(web.id)}
                                                            className="fa fa-trash fs-13px fa-fw me-5px"
                                                        ></i>
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <ListPagination
                            currentPage={currentPage}
                            totalCount={totalCount}
                            entriesPerPage={entriesPerPage}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                </div>
            </div>

            <CustomModal
                isOpen={modalIsOpen}
                onClose={closeModal}
                title={isEditing ? "Edit Length Unit" : "Add Length Unit"}
                showButton={true}
                modalSize="modal-lg"
                minHeight="500px"
                onSubmit={handleSubmit}
            >
                <div className="card border-0">
                    <div className="tab-content p-3">
                        <div className="tab-pane fade show active" id="allTab">
                            <div className="form-floating mb-15px">
                                <input
                                    type="text"
                                    className="form-control h-45px fs-13px"
                                    placeholder="Name"
                                    id="Name"
                                    name="Name"
                                    value={modalData.Name}
                                    onChange={handleChange}
                                />
                                <label
                                    htmlFor="Name"
                                    className="d-flex align-items-center fs-13px text-gray-600"
                                >
                                    Name <span>*</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </CustomModal>
        </div>
    );
};

export default LengthUnits;
